import { Content, GroupField } from '@prismicio/client';
import { RichText } from 'prismic-reactjs';
import {
  AutoCompleteV2,
  IAutoCompleteV2,
} from '@monofront/design-system/components/Form/AutoComplete';
import getConfig from 'next/config';
import { useSuggestedAddresses } from '@monofront/business/hooks';
import { v4 as uuid } from 'uuid';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { FEATURE_FLAG_MAPBOX_AUTO_COMPLETE } from '@monofront/business/constants';
import { ISuggestedAddress } from '@monofront/types';
import { useTranslations } from 'next-intl';
import {
  ErrorText,
  Input,
  LeftIcon,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import Location from '@monofront/design-system/visuals/Icons/Location';
import { SliceComponentProps } from '@prismicio/react';
import useI18n from '@hooks/i18n/useI18n';
import { useRef, useState, type JSX } from 'react';
import { useOnClickOutside } from '@monofront/business/hooks';

const sessionToken = uuid();

export type IPrismicMultiStepForm = {
  steps?: GroupField;
};

const AddressAutoComplete = AutoCompleteV2 as IAutoCompleteV2<{
  street: string;
  postcode: string;
  city: string;
  countryCode: string;
}>;

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();

/**
 * Props for `AddressForm`.
 */
export type AddressFormProps = SliceComponentProps<Content.AddressFormSlice>;

/**
 * Component for "AddressForm" Slices.
 */
const AddressForm = ({
  slice,
  onChange,
}: AddressFormProps & {
  onChange: (value: ISuggestedAddress) => void;
}): JSX.Element => {
  const { isGermanMarket, locale, availableCountryLocale } = useI18n();
  const [hasError, setHasError] = useState(false);
  const [hasSelectedAddress, setHasSelectedAdress] = useState(false);
  const tAccount = useTranslations('Account');
  const [defaultValue, setDefaultValue] = useState('');
  const isMapboxAutocompleteEnabled = useFeatureIsOn(
    FEATURE_FLAG_MAPBOX_AUTO_COMPLETE,
  );
  const {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  } = useSuggestedAddresses({
    mapboxToken,
    locale,
    sessionToken,
    searchableCountry: availableCountryLocale,
  });
  const refContainer = useRef<HTMLInputElement>(null);

  // need to make input as error statement if user blur the input
  // but onBlur is not enough to handle error when we select a suggested address
  // as the on blur is trigerred before the onSuggestedAddressSelect
  useOnClickOutside(refContainer, () => {
    !hasSelectedAddress && setHasError(true);
  });

  const onSuggestedAddressSelect = ({
    label,
    street,
    postcode,
    city,
    countryCode = 'FR',
    type,
  }: ISuggestedAddress) => {
    onChange?.({ street, postcode, city, label, countryCode, type });
    setWrittenStreet('');
    setDefaultValue(label);
    setHasError(false);
    setHasSelectedAdress(true);

    // force hide keyboard on ios browser
    // https://stackoverflow.com/questions/18384373/hide-keyboard-on-ios-device-when-focus-in-input
    (document.activeElement as HTMLElement)?.blur?.();
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div className="limited-width-container tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-8 tw-py-4 tw-align-top desktop:tw-flex-row desktop:tw-items-start desktop:tw-py-8">
        <div className="tw-mx-2 tw-mb-1 tw-max-w-[386px] [&>h2]:!tw-lf-title-sm desktop:[&>h2]:!tw-lf-title-xl [&>h3]:!tw-lf-body-regular-md desktop:tw-mx-0 [&>h2]:tw-text-center [&>h2]:!tw-text-[19px] [&>h2]:desktop:tw-mb-3 [&>h2]:desktop:tw-text-left desktop:[&>h2]:!tw-text-[32px] [&>h3]:tw-text-center [&>h3]:desktop:tw-text-left">
          <RichText render={slice.primary.title} />
          <RichText render={slice.primary.subtitle} />
        </div>
        <div className="tw-w-full desktop:tw-max-w-[486px]">
          {isGermanMarket && !isMapboxAutocompleteEnabled ? (
            <TextInput
              id="street"
              isInvalid={hasError}
              UNSAFE_className="tw-w-full"
            >
              <Input
                autoFocus
                name="address"
                autoComplete="address"
                type="text"
                placeholder={tAccount('suggestedAddressPlaceholderV2')}
                dataTestId="AddressForm-Input"
                onChange={(e) => {
                  setHasError(false);
                  setWrittenStreet(e.target.value);
                }}
              >
                <LeftIcon>
                  <Location size={IconSizes.md} />
                </LeftIcon>
              </Input>
              {hasError && (
                <ErrorText>
                  {tAccount('suggestedAddressRequiredMessageV2')}
                </ErrorText>
              )}
            </TextInput>
          ) : (
            <AddressAutoComplete
              isLoading={isSuggestedAddressLoading}
              writtenStreet={writtenStreet}
              onCustomKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  setHasError(true);
                }
              }}
              onCustomFocus={() => {
                setHasError(false);
              }}
              options={
                writtenStreet?.length && !hasError
                  ? suggestedAddresses?.map(
                      ({
                        label,
                        street,
                        postcode,
                        city,
                        countryCode,
                        type,
                      }) => ({
                        id: label,
                        label,
                        value: {
                          label,
                          street,
                          postcode,
                          city,
                          countryCode: countryCode || 'FR',
                          type,
                        },
                      }),
                    )
                  : undefined
              }
              handleSelect={(value) => {
                onSuggestedAddressSelect(value);
              }}
              renderInput={(params) => (
                <TextInput
                  id="street"
                  isInvalid={hasError}
                  UNSAFE_className="tw-w-full"
                >
                  <Input
                    {...params}
                    autoFocus
                    id="street"
                    name="street"
                    autoComplete="off"
                    type="text"
                    ref={refContainer}
                    value={writtenStreet || defaultValue}
                    placeholder={tAccount('suggestedAddressPlaceholderV2')}
                    dataTestId="AddressForm-Input"
                    onChange={(e) => {
                      setHasError(false);
                      setWrittenStreet(e.target.value);
                      setDefaultValue(e.target.value);
                      setHasSelectedAdress(false);
                    }}
                  >
                    <LeftIcon>
                      <Location size={IconSizes.md} />
                    </LeftIcon>
                  </Input>
                  {hasError && (
                    <ErrorText>
                      {tAccount('suggestedAddressRequiredMessageV2')}
                    </ErrorText>
                  )}
                </TextInput>
              )}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default AddressForm;
